import { Component, OnInit, Input, PLATFORM_ID, Inject, OnDestroy } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Subscription } from 'rxjs';
import { CasMetadataNode } from '../../classes/CasMetadataNode';
import { NavigationService } from '../../services/navigation/navigation.service';
import { ApplicationSectionsList, ApplicationSectionUtils } from '../../classes/ApplicationSection';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { ApplicationState } from '../../classes/ApplicationState';
import { ApplicationService } from '../../services/application/application.service';
import { GiactUtils } from '../../../../common/js/GiactUtils';
import { CPMetadataFilingTypes } from '../../../js/interfaces/CoverpathTypes';
import { ValidationService } from '../../services/validation/validation.service';
import { ValidationErrorMessages } from '../../classes/UnsupportedUseCases';

@Component({
  moduleId: module.id,
  selector: 'application-form-section',
  templateUrl: './application-form-section.component.html',
})
export class ApplicationFormSectionComponent implements OnInit, OnDestroy {
  private sub: Subscription;
  @Input() appSection: CasMetadataNode;
  sectionName: string;
  showSection: boolean = true;
  private appState: ApplicationState;
  isServer: boolean;
  isCurrentSectionPayment: boolean = false;
  currentSection: number;
  paymentInfoError: string;
  isAccountRoutingInfoSame: boolean = false;
  isQuestionLocked: boolean = false;

  constructor(
    private navigationService: NavigationService,
    private applicationService: ApplicationService,
    @Inject(PLATFORM_ID) private platformId
  ) {
    this.appState = applicationService.appState;
    this.isServer = isPlatformServer(platformId);
  }

  async ngOnInit() {
    const cpFilingType = this.appState.controller.customer.getCoverpathFilingType();
    this.sectionName = this.appSection.section;
    if (this.isServer) {
      switch (this.sectionName) {
        case ApplicationSectionsList.CASE_SETUP:
        case 'Payment Method':
        case 'Payment':
        case 'Acknowledgements':
        case 'Verify':
          this.showSection = false;
          break;
        default:
          const flags = {
            mmGiactWebDebitVerificationServiceEnabled:
              this.appState.controller.mmGiactWebDebitVerificationServiceEnabled,
          };
          this.showSection = !ApplicationSectionUtils.shouldDisableSection(
            this.appState.controller.customer.application.primaryPolicy,
            this.sectionName,
            flags
          );
          break;
      }
      return;
    }

    await this.applicationService.runSectionValidation(this.sectionName);

    this.sub = this.navigationService.activeApplicationSection$.subscribe((activeSession) => {
      this.showSection =
        ApplicationSectionUtils.getAllSections(cpFilingType, this.appState)[activeSession] === this.sectionName;
      if (this.showSection) {
        CoverpathUtils.scrollToTop();
        // TO-DO: HL-11229
        // Need to replace setTimeout with AfterViewInit, but currently every application section is being loaded at once
        setTimeout(() => {
          // Focus on first <input> element in the application section if one exists
          const formElement = document.getElementsByClassName('application-form')[0];
          const firstInputElement = formElement.getElementsByTagName('input')[0];
          if (firstInputElement) {
            firstInputElement.focus();
          }
          this.navigationService.afterActiveSectionLoaded$.next(activeSession);
        }, 0);
      }
      this.currentSection = activeSession;
      // CA is still on 2020 filing, payment section name is different
      this.isCurrentSectionPayment =
        cpFilingType === CPMetadataFilingTypes.CP2020
          ? this.sectionName === 'Payment'
          : this.sectionName === ApplicationSectionsList.PAYMENT_METHOD;
      this.isQuestionLocked =
        this.appState.controller.isQuestionLockEnabled &&
        this.appState.controller.customer.application.lockedByAdvisor &&
        this.appState.isAdvisor &&
        this.applicationService.isQuestionLockedSection(this.currentSection);
    });
  }

  isInvalidPaymentInfo() {
    const primaryPolicy = this.appState.controller.customer.application.primaryPolicy;
    this.isAccountRoutingInfoSame = ValidationService.isAccountRoutingInfoSame(primaryPolicy);
    this.paymentInfoError = this.getErrorMessage();
    return (
      this.isAccountRoutingInfoSame ||
      GiactUtils.isInvalidPayment(
        primaryPolicy.isGiactWebDebitValid,
        primaryPolicy.giactWebDebitResponseCode,
        this.appState.controller.mmGiactWebDebitVerificationServiceEnabled
      )
    );
  }

  getErrorMessage() {
    const giactResponseCode = this.appState.controller.customer.application.primaryPolicy.giactWebDebitResponseCode;
    return this.isAccountRoutingInfoSame
      ? ValidationErrorMessages.samePaymentInfoError
      : GiactUtils.getNormalizedGiactMessage(
          this.appState.controller.mmGiactWebDebitVerificationServiceEnabled,
          giactResponseCode
        );
  }

  shouldDisplayPaymentAlert(currentQuestion) {
    return (
      this.isCurrentSectionPayment &&
      //to display the payment error righgt below the questions and before Pre-Authorized Payment Plan (PAPP)
      currentQuestion.prop === 'customer.application.primaryPolicy.paymentAccount' &&
      this.isInvalidPaymentInfo()
    );
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
